import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Mail, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApp } from "@/context/AppContext";
import { errMsg, useApi } from "@/lib/api";

export default function UsersPage() {
  const api = useApi();
  const { me } = useApp();
  const [d, setD] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const load = useCallback(() => api.get("/users").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [api]);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async () => {
    try {
      const r = await api.post("/users/invite", { email, role });
      setEmail("");
      if (r.data.email_delivered_now) toast.success(`Invite emailed to ${r.data.email}`);
      else toast.warning("Invited, but the email couldn't be sent — copy the link and send it yourself");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const resend = async (id) => {
    try {
      const r = await api.post(`/invites/${id}/resend`);
      if (r.data.email_delivered_now) toast.success(`Invite emailed again to ${r.data.email}`);
      else toast.error("The email couldn't be sent — copy the link instead");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const act = async (fn) => {
    try {
      await fn();
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (me?.role === "member") return <p data-testid="users-forbidden">Only firm administrators can manage users.</p>;
  return (
    <div className="max-w-[1100px]">
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">Users &amp; Firm</h1>
      <p className="text-slate-500 mt-3">People in <b className="text-slate-800">{me?.tenant_name}</b>. Only invited emails can access this firm's data. Each invite is emailed a branded sign-up link.</p>
      <div className="panel p-6 mt-10 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="small-label">Invite by email</label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firm.com" className="mt-2 rounded-sm" data-testid="invite-email-input" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-36 rounded-sm" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>{(d?.roles || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={invite} disabled={!email.includes("@")} className="rounded-sm" data-testid="invite-submit-button"><UserPlus className="h-4 w-4 mr-2" />Invite</Button>
      </div>
      <div className="panel mt-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]" data-testid="users-table">
          <thead className="bg-slate-100"><tr className="small-label text-left"><th className="px-5 py-3">Email</th><th>Name</th><th>Role</th><th /></tr></thead>
          <tbody>
            {(d?.users || []).map((u) => (
              <tr key={u.user_id} className="border-t border-slate-100" data-testid={`user-row-${u.user_id}`}>
                <td className="px-5 py-2.5">{u.email}</td>
                <td>{u.name || "—"}</td>
                <td>
                  {u.role === "owner" || u.user_id === me.user_id ? <span className="font-mono text-xs uppercase">{u.role}</span> : (
                    <Select value={u.role} onValueChange={(v) => act(() => api.patch(`/users/${u.user_id}/role`, { role: v }))}>
                      <SelectTrigger className="w-32 h-8 rounded-sm text-xs" data-testid={`user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{d.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </td>
                <td />
              </tr>
            ))}
            {(d?.invites || []).map((i) => (
              <tr key={i.id} className="border-t border-slate-100 text-slate-500" data-testid={`invite-row-${i.id}`}>
                <td className="px-5 py-2.5">{i.email}</td>
                <td className="italic" data-testid={`invite-status-${i.id}`}>
                  invited{i.email_status === "sent" ? " · email sent" : i.email_status === "failed" ? " · email not sent" : ""}
                </td>
                <td className="font-mono text-xs uppercase">{i.role}</td>
                <td className="text-right pr-3 whitespace-nowrap">
                  <Button variant="ghost" size="sm" title="Email the invite again" onClick={() => resend(i.id)} data-testid={`invite-resend-${i.id}`}><Mail className="h-3.5 w-3.5" /></Button>
                  {i.token && (
                    <Button variant="ghost" size="sm" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/sign-up?invite=${i.token}`).then(() => toast.success("Invite link copied — send it to them"))} data-testid={`invite-copy-${i.id}`}><Link2 className="h-3.5 w-3.5" /></Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => act(() => api.delete(`/invites/${i.id}`))} data-testid={`invite-delete-${i.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
